/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Card, Chip, CircularProgress, IconButton,
  Table, TableBody, TableCell, TableContainer, TableRow, TablePagination,
  TextField, InputAdornment, Select, MenuItem, FormControl, InputLabel, Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import { useNavigate } from 'react-router-dom';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import { api } from '../../../api';
import { useListUrlState } from '../../../hooks/useListUrlState';
import { useListKeyboard, ACTIVE_ROW_SX } from 'hooks/useListKeyboard';
import { usePharmacy } from '../../../contexts/PharmacyContext';

const TEAL = '#00798E';
const TH = {
  py: 1.5, px: 2, textTransform: 'uppercase', fontSize: '0.6rem',
  fontWeight: 700, color: '#999', borderBottom: '1.5px solid #f0f2f5',
  whiteSpace: 'nowrap', letterSpacing: '0.06em',
};
const td = (last) => ({
  py: 1.25, px: 2, borderBottom: last ? 'none' : '1px solid rgba(0,0,0,0.05)', fontSize: '0.8rem',
});

const STATUS_COLOR = {
  DRAFT: 'default', RECEIVED: 'success', CANCELLED: 'error', PARTIALLY_RETURNED: 'warning',
};
const PAY_COLOR = { PENDING: 'warning', PARTIAL: 'info', PAID: 'success' };

const fmt = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

function InwardList() {
  const navigate = useNavigate();
  const { selectedPharmacy } = usePharmacy();
  const { readParams, writeParams } = useListUrlState();
  const urlParams = readParams();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(urlParams?.get('search') || '');
  const [status, setStatus] = useState(urlParams?.get('status') || '');
  const [page, setPage] = useState(urlParams ? Math.max(parseInt(urlParams.get('page'), 10) - 1, 0) || 0 : 0);
  const [pageSize] = useState(25);
  const [stats, setStats] = useState({ total: 0, received: 0, draft: 0, pendingPayment: 0 });

  const kb = useListKeyboard({
    rowCount: rows.length,
    enabled: !!selectedPharmacy,
    onOpen: (i) => { if (rows[i]) navigate(`/pharmacy/inward/${rows[i].id}`); },
    onCreate: () => { if (selectedPharmacy) navigate('/pharmacy/inward/new'); },
  });

  const fetchData = useCallback(async () => {
    if (!selectedPharmacy) return;
    setLoading(true);
    try {
      writeParams({ search, status, page: page + 1 });
      const res = await api.pharmacy.listInward({
        pharmacy_id: selectedPharmacy.id,
        search: search || undefined,
        status: status || undefined,
        limit: pageSize,
        offset: page * pageSize,
      });
      const data = res.data || [];
      setRows(data);
      setTotal(res.total || 0);
      setStats({
        total: res.total || 0,
        received: data.filter((r) => r.status === 'RECEIVED').length,
        draft: data.filter((r) => r.status === 'DRAFT').length,
        pendingPayment: data.filter((r) => r.payment?.payment_status === 'PENDING').length,
      });
    } catch { /* handled */ } finally { setLoading(false); }
  }, [selectedPharmacy, search, status, page, pageSize, writeParams]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Banner */}
        <Box sx={{ mb: 2.5, p: 2.5, borderRadius: 2, background: `linear-gradient(135deg, ${TEAL} 0%, #004f5e 100%)`, color: 'white', display: 'flex', alignItems: 'center', gap: 2 }}>
          <LocalShippingIcon sx={{ fontSize: 40, opacity: 0.85 }} />
          <Box>
            <MDTypography variant="h5" sx={{ color: '#fff !important', fontWeight: 800, lineHeight: 1.2 }}>
              Purchase Inward · GRN
            </MDTypography>
            <MDTypography variant="caption" sx={{ color: 'rgba(255,255,255,0.75) !important', fontSize: '0.78rem' }}>
              {selectedPharmacy
                ? `${selectedPharmacy.name} · ${selectedPharmacy.code}`
                : 'Select a pharmacy to begin'}
            </MDTypography>
          </Box>
          {selectedPharmacy && (
            <>
              {[
                { label: 'Total', val: total },
                { label: 'Received', val: stats.received },
                { label: 'Draft', val: stats.draft },
                { label: 'Pay Pending', val: stats.pendingPayment },
              ].map((s) => (
                <Box key={s.label} sx={{ ml: 2.5, textAlign: 'center', display: { xs: 'none', md: 'block' } }}>
                  <MDTypography sx={{ color: '#fff !important', fontWeight: 800, fontSize: '1.3rem', lineHeight: 1 }}>{s.val}</MDTypography>
                  <MDTypography sx={{ color: 'rgba(255,255,255,0.6) !important', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.label}</MDTypography>
                </Box>
              ))}
            </>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="contained" size="small" startIcon={<AddIcon />}
            disabled={!selectedPharmacy}
            sx={{ bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }, textTransform: 'none', fontWeight: 700, color: '#fff !important', backdropFilter: 'blur(4px)' }}
            onClick={() => navigate('/pharmacy/inward/new')}
          >
            New GRN (Alt+N)
          </Button>
        </Box>

        {/* Pharmacy guard */}
        {!selectedPharmacy && (
          <Alert
            severity="warning"
            icon={<LocalPharmacyIcon />}
            sx={{ mb: 2, fontSize: '0.85rem' }}
            action={
              <Button size="small" sx={{ fontWeight: 700, color: TEAL }} onClick={() => {}}>
                Select Pharmacy →
              </Button>
            }
          >
            Please select a pharmacy using the ribbon on the right edge of the screen before accessing inward records.
          </Alert>
        )}

        <Card sx={{ mb: 2 }}>
          <Box sx={{ px: 2, py: 1.5, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              {...kb.searchProps}
              size="small" placeholder="GRN no / supplier / invoice no...  ( / to focus )"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              disabled={!selectedPharmacy}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: '#ccc' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 270, '& .MuiOutlinedInput-root': { height: 34 } }}
            />
            <FormControl size="small" sx={{ minWidth: 145 }} disabled={!selectedPharmacy}>
              <InputLabel>Status</InputLabel>
              <Select value={status} label="Status" onChange={(e) => { setStatus(e.target.value); setPage(0); }}
                sx={{ height: 34 }}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="DRAFT">Draft</MenuItem>
                <MenuItem value="RECEIVED">Received</MenuItem>
                <MenuItem value="PARTIALLY_RETURNED">Partially Returned</MenuItem>
                <MenuItem value="CANCELLED">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Card>

        <Card>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableRow>
                {['GRN No', 'Supplier', 'Inv No / Date', 'Rcv Date', 'Net Amount', 'Status', 'Payment', ''].map((h, i) => (
                  <TableCell key={i} sx={TH}>{h}</TableCell>
                ))}
              </TableRow>
              <TableBody>
                {!selectedPharmacy ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6, color: '#bbb', fontSize: '0.82rem' }}>
                      Select a pharmacy to view GRN records
                    </TableCell>
                  </TableRow>
                ) : loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      <CircularProgress size={24} sx={{ color: TEAL }} />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5, color: '#bbb', fontSize: '0.82rem' }}>
                      No records found
                    </TableCell>
                  </TableRow>
                ) : rows.map((row, i) => {
                  const last = i === rows.length - 1;
                  const rowProps = kb.getRowProps(i);
                  return (
                    <TableRow
                      key={row.id} hover
                      {...rowProps}
                      sx={{ cursor: 'pointer', ...(kb.activeIdx === i ? ACTIVE_ROW_SX : {}) }}
                      onClick={(e) => { rowProps.onClick(e); navigate(`/pharmacy/inward/${row.id}`); }}
                    >
                      <TableCell sx={td(last)}>
                        <Box sx={{ fontFamily: 'monospace', fontWeight: 700, color: TEAL, fontSize: '0.82rem' }}>
                          {row.inward_no}
                        </Box>
                      </TableCell>
                      <TableCell sx={td(last)}>
                        <Box sx={{ fontWeight: 500 }}>{row.supplier?.name || '—'}</Box>
                        {row.supplier?.gst_no && (
                          <Box sx={{ fontSize: '0.67rem', color: '#aaa' }}>{row.supplier.gst_no}</Box>
                        )}
                      </TableCell>
                      <TableCell sx={td(last)}>
                        <Box>{row.supplier_invoice_no || '—'}</Box>
                        <Box sx={{ fontSize: '0.7rem', color: '#aaa' }}>{fmtDate(row.supplier_invoice_date)}</Box>
                      </TableCell>
                      <TableCell sx={{ ...td(last), color: '#555' }}>{fmtDate(row.received_date)}</TableCell>
                      <TableCell sx={{ ...td(last), fontWeight: 600 }}>{fmt(row.net_amount)}</TableCell>
                      <TableCell sx={td(last)}>
                        <Chip
                          label={(row.status || '').replace('_', ' ')} size="small"
                          color={STATUS_COLOR[row.status] || 'default'}
                          sx={{ fontSize: '0.6rem', height: 18, fontWeight: 700 }}
                        />
                      </TableCell>
                      <TableCell sx={td(last)}>
                        {row.payment ? (
                          <Chip
                            label={row.payment.payment_status} size="small"
                            color={PAY_COLOR[row.payment.payment_status] || 'default'}
                            sx={{ fontSize: '0.6rem', height: 18, fontWeight: 700 }}
                          />
                        ) : <Box sx={{ fontSize: '0.7rem', color: '#ccc' }}>—</Box>}
                      </TableCell>
                      <TableCell sx={td(last)}>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/pharmacy/inward/${row.id}`); }}>
                          <VisibilityIcon sx={{ fontSize: 15, color: TEAL }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div" count={total} page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={pageSize} rowsPerPageOptions={[pageSize]}
          />
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default InwardList;
